import { useState } from 'react';
import { Alert, Button, Form, Input, Spin } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthKart } from '../components/AuthKart';
import { apiMesaji, hataDurumu, parolaApi } from '../api/parola';
import { PAROLA_KURALI_IPUCU, parolaKuraliRule } from '../utils/parolaKurali';

const GECERSIZ_BAGLANTI = 'Bağlantı geçersiz ya da süresi dolmuş.';

/** Yalnız 400 bağlantının kendisini reddeder; 429 ve ağ hatası bağlantıyı bozuk göstermemeli. */
function kontrolHatasi(error: unknown): string {
  const durum = hataDurumu(error);
  if (durum === 400) return apiMesaji(error) ?? GECERSIZ_BAGLANTI;
  if (durum === 429) return 'Çok fazla deneme. Birkaç dakika sonra sayfayı yenileyin.';
  return 'Bağlantı şu an denetlenemedi. Birkaç dakika sonra sayfayı yenileyin.';
}

export function ParolaBelirle() {
  const [searchParams] = useSearchParams();
  const belirtec = searchParams.get('t') ?? '';
  const navigate = useNavigate();
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [kaydedildi, setKaydedildi] = useState(false);
  const [hata, setHata] = useState<string | null>(null);

  const dogrulama = useQuery({
    queryKey: ['parola-belirtec', belirtec],
    queryFn: () => parolaApi.belirtecDogrula(belirtec),
    enabled: !!belirtec,
    retry: false,
    staleTime: Infinity,
  });

  const onFinish = async ({ parola }: { parola: string; parolaTekrar: string }) => {
    setKaydediliyor(true);
    setHata(null);
    try {
      await parolaApi.belirle(belirtec, parola);
      setKaydedildi(true);
    } catch (error) {
      setHata(
        hataDurumu(error) === 429
          ? 'Çok fazla deneme. Birkaç dakika sonra tekrar deneyin.'
          : (apiMesaji(error) ?? 'Parola kaydedilemedi. Tekrar deneyin.'),
      );
    } finally {
      setKaydediliyor(false);
    }
  };

  const yeniBaglantiIste = (
    <div style={{ textAlign: 'center' }}>
      <Link to="/sifremi-unuttum">Yeni bağlantı iste</Link>
    </div>
  );

  if (!belirtec || dogrulama.isError) {
    const mesaj = dogrulama.isError ? kontrolHatasi(dogrulama.error) : GECERSIZ_BAGLANTI;
    return (
      <AuthKart altBaslik="Parola belirle">
        <Alert type="error" showIcon title={mesaj} style={{ marginBottom: 16 }} />
        {yeniBaglantiIste}
      </AuthKart>
    );
  }

  if (dogrulama.isPending) {
    return (
      <AuthKart altBaslik="Parola belirle">
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin />
        </div>
      </AuthKart>
    );
  }

  if (kaydedildi) {
    return (
      <AuthKart altBaslik="Parola belirle">
        <Alert type="success" showIcon title="Parolanız kaydedildi." style={{ marginBottom: 16 }} />
        <Button type="primary" block size="large" onClick={() => navigate('/login')}>
          Girişe git
        </Button>
      </AuthKart>
    );
  }

  const { email, amac } = dogrulama.data;
  return (
    <AuthKart altBaslik={amac === 'davet' ? 'Hesabınızı etkinleştirin' : 'Parola belirle'}>
      <p style={{ color: 'rgba(0,0,0,0.65)', marginTop: 0 }}>
        <strong>{email}</strong> için yeni parolanızı belirleyin.
      </p>
      <Form name="parola-belirle" layout="vertical" size="large" onFinish={onFinish}>
        <Form.Item
          name="parola"
          label="Yeni parola"
          extra={PAROLA_KURALI_IPUCU}
          rules={[{ required: true, message: 'Parola gerekli' }, parolaKuraliRule]}
        >
          <Input.Password prefix={<LockOutlined />} autoComplete="new-password" />
        </Form.Item>
        <Form.Item
          name="parolaTekrar"
          label="Yeni parola (tekrar)"
          dependencies={['parola']}
          rules={[
            { required: true, message: 'Parola tekrarı gerekli' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('parola') === value) return Promise.resolve();
                return Promise.reject(new Error('Parolalar eşleşmiyor'));
              },
            }),
          ]}
        >
          <Input.Password prefix={<LockOutlined />} autoComplete="new-password" />
        </Form.Item>
        {hata && <Alert type="error" showIcon title={hata} style={{ marginBottom: 16 }} />}
        <Form.Item>
          <Button type="primary" htmlType="submit" loading={kaydediliyor} block>
            Parolayı kaydet
          </Button>
        </Form.Item>
      </Form>
      <div style={{ textAlign: 'center' }}>
        <Link to="/login">Girişe dön</Link>
      </div>
    </AuthKart>
  );
}
